/**
 * The filing cabinet.
 *
 * Permission slips, the reading log, the newsletter template, the form the
 * office wants twice a year. Things that are printed rather than read on a
 * screen, and that are the teacher's rather than any one class's — so unlike
 * the photo book, nothing here is archived in June. Next year's field trip
 * form is this year's field trip form.
 */
export const DOCUMENT_RULES = {
    /**
     * What a school actually hands a teacher.
     *
     * SVG is not on the list and never should be: it is a script container that
     * would be served from this origin, and this origin holds a class list.
     */
    acceptedMimeTypes: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/heic',
        'image/heif',
    ],
    maxUploadBytes: 20 * 1024 * 1024,
    maxPerTeacher: 500,
    /** How many can be sent to the printer in one go. */
    maxPrintAtOnce: 20,
};
/**
 * Whether the browser can put this on paper by itself.
 *
 * PDFs and images, yes. A Word file, no — nothing in a browser can render
 * one, so the honest answer is a download and a sentence saying why, rather
 * than a Print button that opens a dialog full of nothing.
 */
export function canPrintInBrowser(contentType) {
    return contentType === 'application/pdf' || contentType.startsWith('image/');
}
export function isWordDocument(contentType) {
    return (contentType === 'application/msword' ||
        contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
}
/** 'PDF', 'Word', 'Photo' — what to show beside the name. */
export function documentKind(contentType) {
    if (contentType === 'application/pdf')
        return 'PDF';
    if (isWordDocument(contentType))
        return 'Word';
    if (contentType.startsWith('image/'))
        return 'Image';
    return 'File';
}
/** '2.4 MB', for the line under the name. */
export function fileSizeLabel(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
/**
 * The documents in the order she looks for them.
 *
 * Pinned first, because those are the ones wanted weekly; the rest newest
 * first, because the thing just uploaded is usually the thing being printed.
 */
export function sortDocuments(documents) {
    return [...documents].sort((a, b) => {
        if (a.pinned !== b.pinned)
            return a.pinned ? -1 : 1;
        return b.uploadedAt.localeCompare(a.uploadedAt);
    });
}
/** Name and note, matched on whole words a teacher would actually type. */
export function matchesSearch(document, search) {
    const term = search.trim().toLowerCase();
    if (!term)
        return true;
    return `${document.name} ${document.note} ${document.fileName}`.toLowerCase().includes(term);
}
//# sourceMappingURL=documents.js.map